import { lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { findRoute } from "shared/helpers/routes/findRoute";
import ProtectedRoute from "hub/src/components/auth/ProtectedRoute";
import LoadingPage from "hub/src/components/common/LoadingPage";
import useDeviceInfo from "hub/src/hooks/useDeviceInfo";
import AccessDeniedPage from "hub/src/pages/AccessDeniedPage";
import CheckoutPage from "hub/src/pages/CheckoutPage";
import FormPage from "hub/src/pages/FormPage";
import OverviewPage from "hub/src/pages/OverviewPage";
import PageNotFound from "hub/src/pages/PageNotFound";
import ReviewsVotingPage from "hub/src/pages/reviews/HubReviewsVotingPage";
import SharedUrlsPage from "hub/src/pages/SharedUrlsPage";
import RouteConfigWrapper, {
  type HubRouteConfig,
} from "hub/src/routes/RouteConfigWrapper";
import { isPublicRoute } from "hub/src/utils/routes/isPublicRoute";

// Lazy-loaded conference pages
const conferencePageMapper: Record<
  string,
  React.ComponentType<HubRouteConfig>
> = {
  conferenceLandingPage: lazy(
    () => import("hub/src/pages/conference/ConferenceLandingPage"),
  ),
  conferenceSchedulePage: lazy(
    () => import("hub/src/pages/conference/ConferenceSchedulePage"),
  ),
  conferenceFavoriteEventsPage: lazy(
    () => import("hub/src/pages/conference/ConferenceFavoriteEventsPage"),
  ),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HubRouteConfigAnyOkayHere = any;
const pageComponentMapper: Record<
  string,
  React.ComponentType<HubRouteConfig<HubRouteConfigAnyOkayHere>>
> = {
  overviewPage: lazy(() => import("hub/src/pages/OverviewPage")),
  educationPage: lazy(() => import("hub/src/pages/EducationPage")),
  certificationPage: lazy(() => import("hub/src/pages/CertificationPage")),
  financePage: lazy(() => import("hub/src/pages/finance/FinancePage")),
  invoicePage: lazy(() => import("hub/src/pages/finance/InvoicePage")),
  communicationPage: lazy(() => import("hub/src/pages/CommunicationPage")),
  myFilesPage: lazy(() => import("hub/src/pages/MyFilesPage")),
  directoriesPage: lazy(() => import("hub/src/pages/DirectoriesPage")),
  sharedUrlsPage: lazy(() => import("hub/src/pages/SharedUrlsPage")),
  ssoPage: lazy(() => import("hub/src/pages/sso/SSOPage")),
  submissionsPage: lazy(
    () => import("hub/src/pages/submissions/SubmissionsPage"),
  ),
  renderDownloadPage: lazy(() => import("hub/src/pages/RenderDownloadPage")),
  reviewsPage: lazy(() => import("hub/src/pages/reviews/HubReviewsPage")),
  formPage: lazy(() => import("hub/src/pages/FormPage")),
  accountRecoveryPage: lazy(() => import("hub/src/pages/AccountRecoverPage")),
  loginPage: lazy(() => import("hub/src/pages/LoginPage")),
  contentPage: lazy(() => import("hub/src/pages/ContentPage")),
  storePage: lazy(() => import("hub/src/pages/store/StorePage")),
  checkoutPage: lazy(() => import("hub/src/pages/CheckoutPage")),
  productDetailsPage: lazy(
    () => import("hub/src/pages/store/ProductDetailsPage"),
  ),
  posterGalleryPage: lazy(() => import("hub/src/pages/PosterGalleryPage")),
  posterPreviewPage: lazy(
    () =>
      import(
        "hub/src/components/conference/poster-gallery/HubPosterGalleryPreview"
      ),
  ),
  reviewsVotingPage: lazy(
    () => import("hub/src/pages/reviews/HubReviewsVotingPage"),
  ),
  reportPage: lazy(() => import("hub/src/pages/ReportPage")),
  workflowInstancePage: lazy(
    () => import("hub/src/pages/WorkflowInstancePage"),
  ),
  chatsPage: lazy(() => import("hub/src/pages/ChatsPage")),
  chatDetailsPage: lazy(() => import("hub/src/pages/ChatDetailsPage")),
  navigationPage: lazy(() => import("hub/src/pages/NavigationPage")),
  ...conferencePageMapper,
};

const UserHubRoutes = () => {
  const session = useCustomSession();
  const device = useDeviceInfo();

  const defaultAppRoute =
    session.society?.societySettingsPublic?.defaultAppRoute;

  if (session.society === null) {
    return <LoadingPage />;
  }

  return (
    <Suspense fallback={<LoadingPage />}>
      <Switch>
        <Route exact path="/access-denied">
          <RouteConfigWrapper>
            <AccessDeniedPage />
          </RouteConfigWrapper>
        </Route>
        {defaultAppRoute ? (
          <Route exact path="/">
            <RouteConfigWrapper>
              <Redirect push to={defaultAppRoute} />
            </RouteConfigWrapper>
          </Route>
        ) : (
          <ProtectedRoute
            exact
            path="/"
            render={() => {
              const overViewPage = findRoute(session?.society?.routes ?? [], {
                componentName: "overviewPage",
              });
              if (overViewPage?.url) {
                return <Redirect push to={overViewPage.url} />;
              }
              return (
                <RouteConfigWrapper>
                  <OverviewPage />
                </RouteConfigWrapper>
              );
            }}
          />
        )}
        {session.society?.routes?.flatMap((route) => {
          const allRoutes = [route];
          if (
            route.childrenAppRouteConfig?.length &&
            route.childrenAppRouteConfig?.length > 0
          ) {
            route.childrenAppRouteConfig.forEach((childRoute) => {
              allRoutes.push({ ...childRoute, parentAppRouteConfig: route });
            });
          }
          return allRoutes.map((nestedRoute) => {
            if (
              !nestedRoute.externalRoute &&
              nestedRoute.url &&
              nestedRoute.componentName
            ) {
              const Component = pageComponentMapper[nestedRoute.componentName];

              if (isPublicRoute(nestedRoute)) {
                return (
                  <Route
                    key={nestedRoute.id}
                    path={nestedRoute.url}
                    exact
                    render={() => (
                      <RouteConfigWrapper routeConfig={nestedRoute}>
                        <Component />
                      </RouteConfigWrapper>
                    )}
                  />
                );
              } else {
                return (
                  <ProtectedRoute
                    key={nestedRoute.id}
                    path={nestedRoute.url}
                    exact
                    routeConfig={nestedRoute}
                    render={() => (
                      <RouteConfigWrapper routeConfig={nestedRoute}>
                        <Component />
                      </RouteConfigWrapper>
                    )}
                  />
                );
              }
            }
            return null;
          });
        })}
        <ProtectedRoute exact path="/form/:formId">
          <RouteConfigWrapper>
            <FormPage />
          </RouteConfigWrapper>
        </ProtectedRoute>
        <ProtectedRoute exact path="/shared-urls/:fileIdOrSlug">
          <RouteConfigWrapper>
            <SharedUrlsPage />
          </RouteConfigWrapper>
        </ProtectedRoute>

        <ProtectedRoute
          exact
          path="/reviews/:reviewSessionId/instance/:reviewInstanceId"
        >
          <RouteConfigWrapper>
            <ReviewsVotingPage />
          </RouteConfigWrapper>
        </ProtectedRoute>
        {device.isWeb && (
          <ProtectedRoute exact path="/payment/:invoiceId">
            <RouteConfigWrapper>
              <CheckoutPage />
            </RouteConfigWrapper>
          </ProtectedRoute>
        )}
        <Route exact path="/account/new">
          <RouteConfigWrapper>
            <FormPage />
          </RouteConfigWrapper>
        </Route>
        {/* Allow Cypress to inject its UI at /__/ without rendering 404 */}
        <Route path="/__">{null}</Route>
        <Route>
          <RouteConfigWrapper>
            <PageNotFound />
          </RouteConfigWrapper>
        </Route>
      </Switch>
    </Suspense>
  );
};

export default UserHubRoutes;
